'use client';

import React, { useState } from 'react';
import { Heading, Text, Card, CardContent, Button, Switch } from '../../components/ui/design-system';

export default function PricingPage() {
  const [annualBilling, setAnnualBilling] = useState(true);
  
  return (
    <main className="pt-32 pb-24">
      <div className="max-w-6xl mx-auto px-6 md:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <Heading level={1} className="mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary-500 to-primary-600 dark:from-primary-400 dark:to-primary-500">Pricing</Heading>
          <Text size="lg" className="text-gray-600 dark:text-gray-300">
            Choose the perfect PAVISHA plan for your business needs
          </Text>
        </div>
        
        {/* Billing Toggle */}
        <div className="flex justify-center items-center gap-3 mb-16">
          <Text className="font-medium text-gray-700 dark:text-gray-300">Monthly</Text>
          <Switch
            checked={annualBilling}
            onChange={() => setAnnualBilling(!annualBilling)}
            className="relative inline-flex h-6 w-11 items-center premium-radius-full"
          />
          <div className="flex items-center gap-1.5">
            <Text className="font-medium text-gray-700 dark:text-gray-300">Annual</Text>
            <span className="inline-flex items-center px-2 py-0.5 premium-radius-sm text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
              Save 20%
            </span>
          </div>
        </div>
        
        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-24">
          {/* Basic Plan */}
          <Card className="border border-gray-200 dark:border-gray-700">
            <CardContent className="p-8">
              <Heading level={3} className="mb-2 text-gray-900 dark:text-white">Basic</Heading>
              <Text className="mb-6 text-gray-500 dark:text-gray-400">Perfect for small businesses just getting started</Text>
              
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900 dark:text-white">${annualBilling ? '19' : '24'}</span>
                <span className="text-gray-500 dark:text-gray-400">/mo</span>
                {annualBilling && (
                  <Text className="text-sm text-gray-500 dark:text-gray-400 mt-1">billed annually (${19 * 12} total)</Text>
                )}
              </div>
              
              <Button 
                variant="outline"
                size="lg" 
                className="w-full mb-8"
                onClick={() => window.location.href = '/auth/register?plan=basic'}
              >
                Get Started
              </Button>
              
              <Text className="font-medium text-gray-900 dark:text-white mb-4">Includes:</Text>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-primary-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <Text className="text-gray-600 dark:text-gray-300">Up to 100 products</Text>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-primary-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <Text className="text-gray-600 dark:text-gray-300">2% transaction fee</Text>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-primary-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <Text className="text-gray-600 dark:text-gray-300">Basic analytics</Text>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-primary-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <Text className="text-gray-600 dark:text-gray-300">Community support</Text>
                </li>
              </ul>
            </CardContent>
          </Card>
          
          {/* Pro Plan */}
          <Card className="border-2 border-primary-500 dark:border-primary-400 shadow-lg relative">
            <div className="absolute -top-4 inset-x-0 flex justify-center">
              <span className="bg-primary-500 text-white px-3 py-1 premium-radius-full text-sm font-medium">Most Popular</span>
            </div>
            <CardContent className="p-8">
              <Heading level={3} className="mb-2 text-gray-900 dark:text-white">Pro</Heading>
              <Text className="mb-6 text-gray-500 dark:text-gray-400">Ideal for growing businesses with more demands</Text>
              
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900 dark:text-white">${annualBilling ? '49' : '59'}</span>
                <span className="text-gray-500 dark:text-gray-400">/mo</span>
                {annualBilling && (
                  <Text className="text-sm text-gray-500 dark:text-gray-400 mt-1">billed annually (${49 * 12} total)</Text>
                )}
              </div>
              
              <Button 
                variant="primary"
                size="lg" 
                className="w-full mb-8"
                onClick={() => window.location.href = '/auth/register?plan=pro'}
              >
                Get Started
              </Button>
              
              <Text className="font-medium text-gray-900 dark:text-white mb-4">Everything in Basic, plus:</Text>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-primary-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <Text className="text-gray-600 dark:text-gray-300">Up to 1,000 products</Text>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-primary-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <Text className="text-gray-600 dark:text-gray-300">1% transaction fee</Text>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-primary-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <Text className="text-gray-600 dark:text-gray-300">Advanced analytics</Text>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-primary-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <Text className="text-gray-600 dark:text-gray-300">Priority email support</Text>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-primary-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <Text className="text-gray-600 dark:text-gray-300">Custom domain</Text>
                </li>
              </ul>
            </CardContent>
          </Card>
          
          {/* Enterprise Plan */}
          <Card className="border border-gray-200 dark:border-gray-700">
            <CardContent className="p-8">
              <Heading level={3} className="mb-2 text-gray-900 dark:text-white">Enterprise</Heading>
              <Text className="mb-6 text-gray-500 dark:text-gray-400">Advanced features for larger businesses</Text>
              
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900 dark:text-white">${annualBilling ? '149' : '179'}</span>
                <span className="text-gray-500 dark:text-gray-400">/mo</span>
                {annualBilling && (
                  <Text className="text-sm text-gray-500 dark:text-gray-400 mt-1">billed annually (${149 * 12} total)</Text>
                )}
              </div>
              
              <Button 
                variant="outline"
                size="lg" 
                className="w-full mb-8"
                onClick={() => window.location.href = '/auth/register?plan=enterprise'}
              >
                Get Started
              </Button>
              
              <Text className="font-medium text-gray-900 dark:text-white mb-4">Everything in Pro, plus:</Text>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-primary-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <Text className="text-gray-600 dark:text-gray-300">Unlimited products</Text>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-primary-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <Text className="text-gray-600 dark:text-gray-300">0.5% transaction fee</Text>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-primary-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <Text className="text-gray-600 dark:text-gray-300">Dedicated account manager</Text>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-primary-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <Text className="text-gray-600 dark:text-gray-300">API access</Text>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-primary-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <Text className="text-gray-600 dark:text-gray-300">White labeling</Text>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
        
        {/* FAQs */}
        <div className="max-w-3xl mx-auto">
          <Heading level={2} className="text-center mb-12 text-gray-900 dark:text-white">Frequently Asked Questions</Heading>
          
          <div className="space-y-8">
            <div>
              <Heading level={3} className="mb-2 text-gray-900 dark:text-white">Can I upgrade or downgrade my plan later?</Heading>
              <Text className="text-gray-600 dark:text-gray-300">
                Yes, you can upgrade or downgrade your plan at any time. If you upgrade, the new rate will be charged on your next billing cycle. If you downgrade, your new rate will apply after the current billing period ends.
              </Text>
            </div>
            
            <div>
              <Heading level={3} className="mb-2 text-gray-900 dark:text-white">What payment methods do you accept?</Heading>
              <Text className="text-gray-600 dark:text-gray-300">
                We accept all major credit cards (Visa, MasterCard, American Express), PayPal, and for annual plans, we can also accept bank transfers.
              </Text>
            </div>
            
            <div>
              <Heading level={3} className="mb-2 text-gray-900 dark:text-white">Do you offer a free trial?</Heading>
              <Text className="text-gray-600 dark:text-gray-300">
                Yes, all plans come with a 14-day free trial. No credit card is required to start your trial.
              </Text>
            </div>
            
            <div>
              <Heading level={3} className="mb-2 text-gray-900 dark:text-white">What happens when I hit my product limit?</Heading>
              <Text className="text-gray-600 dark:text-gray-300">
                You'll receive notifications as you approach your product limit. Once you hit the limit, you'll need to upgrade to a higher plan to add more products or archive some existing ones to make room for new products.
              </Text>
            </div>
          </div>
        </div>
        
        {/* CTA */}
        <div className="mt-24 text-center">
          <Heading level={2} className="mb-6 text-gray-900 dark:text-white">Need a custom solution?</Heading>
          <Text className="mb-8 max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
            Contact our sales team for a custom plan tailored to your specific business needs.
          </Text>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="primary"
              size="lg"
              onClick={() => window.location.href = '/contact'}
            >
              Contact Sales
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => window.location.href = '/book-demo'}
            >
              Schedule a Demo
            </Button>
          </div>
        </div>
      </div>
    </main>
  );
} 